import { gql, useMutation, useQuery } from '@apollo/client'
import sumBy from 'lodash/sumBy'
import { useEffect, useState } from 'react'

import {
  AnswerItemCollectMissionMutation,
  AnswerItemCollectMissionMutationVariables,
  AnswerQuizMissionMutation,
  AnswerQuizMissionMutationVariables,
  BlockUser,
  BlockUserMutation,
  BlockUserMutationVariables,
  CommentBoxFragmentDoc,
  CommentsConnectionFragmentDoc,
  CreateMissionCommentMutation,
  CreateMissionCommentMutationVariables,
  DeleteMissionCommentMutation,
  DeleteMissionCommentMutationVariables,
  LikeMissionCommentMutation,
  LikeMissionCommentMutationVariables,
  MissionDetailFragmentDoc,
  MissionDetailQuery,
  MissionDetailQueryVariables,
  QuizResultConnectionFragmentDoc,
  UnlikeMissionCommentMutation,
  UnlikeMissionCommentMutationVariables,
} from '@/clients/utoniq-core/schema'
import { GpsClientInfo } from '@/components/organisms/gps'
import { config } from '@/config'
import { useMessage, useReplyToComment, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { openReportForm } from '@/core/googleForm'
import { useLocationWatcher } from '@/core/native/geolocation'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages, generatePath } = useRouter<{
    missionId: string
    artistId?: string
  }>()
  const { analytics } = useAnalytics()
  const { showError } = useMessage()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [incorrect, setIncorrect] = useState<boolean>(false)
  const [sheet, setSheet] = useState<number>()
  const {
    data: { replyToDisplayName, replyToAccountId, replyToCommentId },
    onReplyToReply,
    onRemoveReplyToReply,
  } = useReplyToComment()

  const { data, loading, fetchMore, refetch } = useQuery<
    MissionDetailQuery,
    MissionDetailQueryVariables
  >(QUERY, {
    variables: { missionId: router.query.missionId },
    returnPartialData: true,
  })

  const { permission, currentLocation, requestPermission, watch, unwatch } =
    useLocationWatcher()

  useEffect(() => {
    if (!data?.mission?.gpsData) return
    requestPermission().catch(showError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.mission?.gpsData, requestPermission])

  useEffect(() => {
    if (!permission) return
    watch().catch(showError)
    return () => {
      unwatch().catch(showError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, unwatch, watch])

  const [submitAnswer, { loading: submittingAnswer }] = useMutation<
    AnswerQuizMissionMutation,
    AnswerQuizMissionMutationVariables
  >(UPDATE_ANSWER_QUIZ_MISSION, {
    onCompleted(data) {
      if (!data?.answerQuizMission.correct) {
        analytics?.track('impression_incorrect', {
          applicationKey: config.applicationKey,
          missionId: router.query.missionId,
          artistId: data?.answerQuizMission.mission.artist.id,
        })
        setOpenModal(true)
        return
      }
      analytics?.track('impression_correct', {
        applicationKey: config.applicationKey,
        missionId: router.query.missionId,
        artistId: data?.answerQuizMission.mission.artist.id,
      })
      return router.push(
        router.query.artistId
          ? generatePath(routerPages.ArtistMissionAccomplished, {
              missionId: router.query.missionId,
              artistId: router.query.artistId,
            })
          : generatePath(routerPages.MissionAccomplished, {
              missionId: router.query.missionId,
            })
      )
    },
  })

  const [answerItemCollectMission, { loading: submittingAnswerItemCollect }] =
    useMutation<
      AnswerItemCollectMissionMutation,
      AnswerItemCollectMissionMutationVariables
    >(UPDATE_ANSWER_ITEM_COLLECT_MISSION, {
      onCompleted(data) {
        if (!data?.answerItemCollectMission.correct) {
          analytics?.track('impression_incorrect', {
            applicationKey: config.applicationKey,
            missionId: router.query.missionId,
            artistId: data?.answerItemCollectMission.mission.artist.id,
          })
          const requiredCount =
            sumBy(
              data?.answerItemCollectMission.lackItems ?? [],
              'requiredCount'
            ) ?? 0
          setSheet(requiredCount)
          setOpenModal(true)
          return
        }
        analytics?.track('impression_correct', {
          applicationKey: config.applicationKey,
          missionId: router.query.missionId,
          artistId: data?.answerItemCollectMission.mission.artist.id,
        })
        return router.push(
          router.query.artistId
            ? generatePath(routerPages.ArtistMissionAccomplished, {
                missionId: data.answerItemCollectMission.missionId,
                artistId: router.query.artistId,
              })
            : generatePath(routerPages.MissionAccomplished, {
                missionId: data.answerItemCollectMission.missionId,
              })
        )
      },
    })

  const [createMissionComment] = useMutation<
    CreateMissionCommentMutation,
    CreateMissionCommentMutationVariables
  >(CREATE_MISSION_COMMENT)

  const [likeMissionComment] = useMutation<
    LikeMissionCommentMutation,
    LikeMissionCommentMutationVariables
  >(LIKE_MISSION_COMMENT)

  const [unlikeMissionComment] = useMutation<
    UnlikeMissionCommentMutation,
    UnlikeMissionCommentMutationVariables
  >(UNLIKE_MISSION_COMMENT)

  const [deleteMissionComment] = useMutation<
    DeleteMissionCommentMutation,
    DeleteMissionCommentMutationVariables
  >(DELETE_MISSION_COMMENT)

  const [blockUser] = useMutation<
    BlockUserMutation,
    BlockUserMutationVariables
  >(BlockUser)

  const isNextFetch = !!data?.mission

  return {
    state: {
      loading: loading && !isNextFetch,
      submittingAnswer: submittingAnswer || submittingAnswerItemCollect,
      openModal,
      sheet,
      incorrect,
      disabledItem: true,
      user: data?.user ?? null,
      comments: data?.mission?.comments ?? null,
      hasNextPage: !!data?.mission?.comments?.pageInfo?.hasNextPage,
      mission: (data?.mission ?? null) as State['mission'],
      isLinked: false,
      gpsClientInfo: {
        permission,
        currentLocation,
      } as GpsClientInfo,
      isGpsMission: !!data?.mission?.gpsData,
      replyToDisplayName,
    },
    actions: {
      async onSubmitAnswer(values) {
        analytics?.track('click_answerMission', {
          applicationKey: config.applicationKey,
          missionId: router.query.missionId,
          artistId: data?.mission?.artist.id,
        })
        setIncorrect(true)
        await submitAnswer({
          variables: {
            answerQuizMissionMissionId: router.query.missionId,
            answerQuizMissionAnswer: values.answer,
          },
        })
      },
      async onSubmitTweet() {},
      async onSubmitAnswerItem() {
        await answerItemCollectMission({
          variables: { missionId: router.query.missionId },
        })
      },
      async onFollow() {},
      onCloseModal() {
        setOpenModal(false)
      },
      onReplyToReply,
      onRemoveReplyToReply,
      async onFetchMore() {
        await fetchMore({
          variables: {
            missionId: router.query.missionId,
            pageInput: {
              cursor: data?.mission?.comments?.pageInfo.endCursor,
              limit: 30,
            },
          },
        })
      },
      async onReply(inputs) {
        await createMissionComment({
          variables: {
            id: router.query.missionId,
            inputs: {
              artistId: data?.mission?.artist.id ?? '',
              replyToId: replyToCommentId,
              replyToAccountId,
              ...inputs,
            },
          },
        }).then(() => {
          analytics?.track('click_sendComment', {
            applicationKey: config.applicationKey,
            missionId: router.query.missionId,
            artistId: data?.mission?.artist.id ?? '',
            name: 'missionDetail',
          })
        })
        onRemoveReplyToReply()
      },
      async onLikeOrUnlikeComment({ isLike, commentId, id, likeCount }) {
        if (isLike) {
          await unlikeMissionComment({
            variables: {
              inputs: { commentId, id: id ?? router.query.missionId },
            },
            optimisticResponse({ inputs: { commentId } }) {
              return {
                unlikeMissionComment: {
                  __typename: 'Comment',
                  id: commentId,
                  isLike: false,
                  likeCount: --likeCount,
                },
              }
            },
          }).then(() => {
            analytics?.track('click_unlikeComment', {
              applicationKey: config.applicationKey,
              missionId: router.query.missionId,
              artistId: data?.mission?.artist.id,
              name: 'missionDetail',
            })
          })
          return
        }
        await likeMissionComment({
          variables: {
            inputs: { commentId, id: id ?? router.query.missionId },
          },
          optimisticResponse({ inputs: { commentId } }) {
            return {
              likeMissionComment: {
                __typename: 'Comment',
                id: commentId,
                isLike: true,
                likeCount: ++likeCount,
              },
            }
          },
        }).then(() => {
          analytics?.track('click_likeComment', {
            applicationKey: config.applicationKey,
            missionId: router.query.missionId,
            artistId: data?.mission?.artist.id,
            name: 'missionDetail',
          })
        })
      },
      async onDeleteComment(commentId) {
        await deleteMissionComment({
          variables: {
            commentId,
            id: router.query.missionId,
          },
        }).then(() => {
          analytics?.track('click_deleteComment', {
            applicationKey: config.applicationKey,
            missionId: router.query.missionId,
            artistId: data?.mission?.artist.id,
            name: 'missionDetail',
          })
        })
      },
      async onReportUser(contentId, userId, accountId) {
        await openReportForm({
          email: data?.user?.email ?? '',
          contentType: 'mission-comments',
          contentId,
          userId,
          accountId,
        })
      },
      async onBlockUser(userId) {
        await blockUser({ variables: { id: userId } })
        await refetch({ missionId: router.query.missionId })
      },
    },
  }
})

const QUERY = gql`
  query MissionDetail($missionId: ID!, $pageInput: PageInput) {
    mission(id: $missionId) {
      ...MissionDetail
      reward {
        item {
          item {
            id
            title
            description
            type
            hideSalesNumber
            rarity {
              id
              name
            }
            rarityIconImage {
              uri
              smallUri
            }
            issuedNumber
            thumbnail {
              front {
                uri
                smallUri
              }
            }
          }
        }
        gachaTicket {
          count
          gachaTicket {
            id
            name
            description
            thumbnails {
              standard {
                uri
              }
            }
          }
        }
      }
      comments(pageInput: $pageInput) {
        ...CommentsConnection
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
      email
      displayName
      avatar {
        uri
        smallUri
      }
    }
  }
  ${CommentsConnectionFragmentDoc}
  ${MissionDetailFragmentDoc}
`

const UPDATE_ANSWER_QUIZ_MISSION = gql`
  mutation AnswerQuizMission(
    $answerQuizMissionMissionId: String!
    $answerQuizMissionAnswer: String!
  ) {
    answerQuizMission(
      missionId: $answerQuizMissionMissionId
      answer: $answerQuizMissionAnswer
    ) {
      ...QuizResultConnection
    }
  }
  ${QuizResultConnectionFragmentDoc}
`

const UPDATE_ANSWER_ITEM_COLLECT_MISSION = gql`
  mutation AnswerItemCollectMission($missionId: String!) {
    answerItemCollectMission(missionId: $missionId) {
      missionId
      correct
      lackItems {
        itemId
        requiredCount
        item {
          id
          title
          hideSalesNumber
        }
      }
      mission {
        id
        artist {
          id
        }
        isAccomplished
      }
    }
  }
`

const CREATE_MISSION_COMMENT = gql`
  mutation CreateMissionComment($id: String!, $inputs: CommunityCommentInput!) {
    createMissionComment(id: $id, inputs: $inputs) {
      ...CommentBox
    }
  }
  ${CommentBoxFragmentDoc}
`

const LIKE_MISSION_COMMENT = gql`
  mutation LikeMissionComment($inputs: CommunityCommentLikeUnlikeInput!) {
    likeMissionComment(inputs: $inputs) {
      id
      isLike
      likeCount
    }
  }
`

const UNLIKE_MISSION_COMMENT = gql`
  mutation UnlikeMissionComment($inputs: CommunityCommentLikeUnlikeInput!) {
    unlikeMissionComment(inputs: $inputs) {
      id
      isLike
      likeCount
    }
  }
`

const DELETE_MISSION_COMMENT = gql`
  mutation DeleteMissionComment($id: String!, $commentId: String!) {
    deleteMissionComment(id: $id, commentId: $commentId) {
      ...CommentBox
    }
  }
  ${CommentBoxFragmentDoc}
`
